<template>
  <div class="gpx-place-autocomplete w-full" :aria-busy="loading">
    <label
      v-if="label"
      class="mb-1 block text-xs font-medium text-gpx_gray-200"
      :for="inputId"
    >
      {{ label }}
    </label>
    <component :is="fieldWrapper" class="w-full">
      <InputIcon v-if="showSearchIcon" class="pi pi-search" />
      <AutoComplete
        ref="autocompleteRef"
        class="w-full"
        :input-id="inputId"
        :model-value="modelValue"
        option-label="label"
        data-key="id"
        :suggestions="suggestions"
        :placeholder="placeholder"
        :disabled="disabled"
        :loading="loading"
        :invalid="invalid"
        :min-length="0"
        :complete-on-focus="completeOnFocus"
        show-clear
        append-to="body"
        :input-class="mergedInputClass"
        @complete="onComplete"
        @update:model-value="onUpdateModelValue"
        @clear="onClear"
      >
        <template #option="slotProps">
          <div class="flex flex-col py-0.5 text-left">
            <span class="text-sm text-gpx_black-100">{{ slotProps.option.label }}</span>
            <span v-if="slotProps.option.subtitle" class="text-xs text-gpx_gray-200">{{
              slotProps.option.subtitle
            }}</span>
          </div>
        </template>
      </AutoComplete>
    </component>
    <p v-if="error" class="mt-1 text-xs text-red-600">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import AutoComplete from 'primevue/autocomplete'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'

export interface PlaceAutocompleteSuggestion {
  id: string
  label: string
  subtitle?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    suggestions: PlaceAutocompleteSuggestion[]
    loading?: boolean
    error?: string | null
    /** Red border / PrimeVue invalid state (e.g. validation). */
    invalid?: boolean
    placeholder?: string
    label?: string
    disabled?: boolean
    inputId?: string
    /** When true, focusing the input re-emits `complete` with the current query so suggestions can show again (pair with `usePlaceAutocomplete`). */
    completeOnFocus?: boolean
    /** Search icon on the left via PrimeVue `IconField` + `InputIcon` (theme adds input padding). */
    showSearchIcon?: boolean
  }>(),
  {
    loading: false,
    error: null,
    invalid: false,
    placeholder: '',
    disabled: false,
    inputId: 'place-autocomplete-input',
    completeOnFocus: true,
    showSearchIcon: true,
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  select: [item: PlaceAutocompleteSuggestion]
  clear: []
}>()

const fieldWrapper = computed(() => (props.showSearchIcon ? IconField : 'div'))

/** With `IconField`, `@primeuix/styles` adds `padding-inline-start` for the input; without icon, use normal `pl-3`. */
const inputClassBase =
  'w-full rounded-[6px] border border-gpx_gray-800 bg-white py-[11px] pr-10 text-sm text-lgmx_black-100 placeholder:text-gpx_gray-200 focus:border-gpx_gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gpx_gray-900 disabled:text-gpx_gray-300'

const mergedInputClass = computed(() => {
  const base = props.showSearchIcon ? inputClassBase : `${inputClassBase} pl-3`
  if (!props.invalid) return base
  return `${base} !border-gpx_red-200 focus:!border-gpx_red-200`
})

function onComplete(event: { query: string }) {
  emit('update:modelValue', event.query)
}

function onUpdateModelValue(val: string | PlaceAutocompleteSuggestion | null | undefined) {
  if (val === null || val === undefined) {
    emit('update:modelValue', '')
    return
  }
  if (typeof val === 'object' && val !== null && 'id' in val) {
    emit('select', val as PlaceAutocompleteSuggestion)
    emit('update:modelValue', val.label)
    return
  }
  emit('update:modelValue', typeof val === 'string' ? val : '')
}

function onClear() {
  emit('clear')
}

const autocompleteRef = ref<{ $el?: HTMLElement } | null>(null)

function focusInput(): boolean {
  if (props.disabled) return false
  if (props.inputId) {
    const byId = document.getElementById(props.inputId)
    if (byId instanceof HTMLInputElement) {
      byId.focus()
      return true
    }
  }
  const root = autocompleteRef.value?.$el
  if (root instanceof HTMLInputElement) {
    root.focus()
    return true
  }
  if (root instanceof HTMLElement) {
    const input = root.querySelector<HTMLInputElement>('input:not([disabled])')
    if (input) {
      input.focus()
      return true
    }
  }
  return false
}

defineExpose({
  focusInput,
})
</script>
